import axios from 'Src/helpers/api/axiosInstance';
import { AppUser } from 'Src/views/App/App';

const config = (user: AppUser) => ({ headers: { usid: user.roleSessionKey }, params: {
  ...((user as any).tournamentCommunicationsUnionId ? {participantUnionId: (user as any).tournamentCommunicationsUnionId} : {})
} });
const base = (user: AppUser, tournamentId: string) =>
  `${window.apiBase}/i/schools/${user.activeSchoolId}/tournaments/${tournamentId}/communications`;

const resource = <T>(data: T | { items: T }): T => {
  let value: any = data;
  while (value && typeof value === 'object' && !Array.isArray(value) && Object.prototype.hasOwnProperty.call(value, 'items')) value = value.items;
  return value as T;
};

const resourceList = <T>(data: T[] | { items: T[] }): T[] => {
  const value = resource(data);
  return Array.isArray(value) ? value : [];
};

export const getTournamentCommunicationsInbox = (user: AppUser, params?: any) =>
  axios
    .get(`${window.apiBase}/i/schools/${user.activeSchoolId}/tournament-communications`, { ...config(user), params })
    .then(response => resourceList(response.data));
export const getTournamentCommunicationTargets = (user: AppUser) =>
  axios.get(`${window.apiBase}/i/schools/${user.activeSchoolId}/tournament-communication-targets`, config(user)).then(response => resourceList(response.data));

export const getTournamentConversations = (user: AppUser, tournamentId: string, params?: any) =>
  axios.get(base(user, tournamentId), { ...config(user), params: {...config(user).params, ...params} }).then(response => resourceList(response.data));
export const getTournamentCommunicationAudiences = (user: AppUser, tournamentId: string) =>
  axios.get(`${base(user, tournamentId)}/audiences`, config(user)).then(response => resourceList(response.data));
export const getTournamentCommunicationConfig = (user: AppUser, tournamentId: string) =>
  axios.get(`${base(user, tournamentId)}/config`, config(user)).then(response => resource(response.data));
export const createTournamentPrivateConversation = (
  user: AppUser,
  tournamentId: string,
  counterpartySchoolId?: string
) =>
  axios
    .post(`${base(user, tournamentId)}/private`, { counterpartySchoolId }, config(user))
    .then(response => resource(response.data));
export const createTournamentGroupConversation = (user: AppUser, tournamentId: string, data: { title: string; schoolIds?: string[]; audience?: string }) =>
  axios.post(`${base(user, tournamentId)}/group`, data, config(user)).then(response => resource(response.data));
export const createTournamentAnnouncement = (user: AppUser, tournamentId: string, data: any) =>
  axios.post(`${base(user, tournamentId)}/announcements`, data, config(user)).then(response => resource(response.data));
export const getTournamentConversationMessages = (
  user: AppUser,
  tournamentId: string,
  conversationId: string,
  params?: any
) =>
  axios
    .get(`${base(user, tournamentId)}/${conversationId}/messages`, { ...config(user), params: {...config(user).params, ...params} })
    .then(response => resourceList(response.data));
export const updateTournamentGroupSchools = (user: AppUser, tournamentId: string, conversationId: string, schoolIds: string[]) =>
  axios.put(`${base(user, tournamentId)}/${conversationId}/schools`, { schoolIds }, config(user)).then(response => resource(response.data));
export const updateTournamentAnnouncement = (user: AppUser, tournamentId: string, conversationId: string, data: { title: string; text: string }) =>
  axios.put(`${base(user, tournamentId)}/${conversationId}/announcement`, data, config(user)).then(response => resource(response.data));
export const deleteTournamentAnnouncement = (user: AppUser, tournamentId: string, conversationId: string) =>
  axios.delete(`${base(user, tournamentId)}/${conversationId}/announcement`, config(user)).then(response => resource(response.data));
export const getTournamentMentionCandidates = (user: AppUser, tournamentId: string, conversationId: string) =>
  axios
    .get(`${base(user, tournamentId)}/${conversationId}/mention-candidates`, config(user))
    .then(response => resourceList(response.data));
export const sendTournamentConversationMessage = (
  user: AppUser,
  tournamentId: string,
  conversationId: string,
  text: string,
  replyToMessageId?: string,
  mentions: any[] = []
) =>
  axios
    .post(
      `${base(user, tournamentId)}/${conversationId}/messages`,
      { text, replyToMessageId, mentions, clientMessageId: `${Date.now()}-${Math.random()}` },
      config(user)
    )
    .then(response => resource(response.data));
export const markTournamentConversationRead = (user: AppUser, tournamentId: string, conversationId: string) =>
  axios.put(`${base(user, tournamentId)}/${conversationId}/read`, {}, config(user)).then(response => resource(response.data));
export const updateTournamentConversationStatus = (
  user: AppUser,
  tournamentId: string,
  conversationId: string,
  status: string
) =>
  axios
    .put(`${base(user, tournamentId)}/${conversationId}/status`, { status }, config(user))
    .then(response => resource(response.data));
export const renameTournamentConversation = (
  user: AppUser,
  tournamentId: string,
  conversationId: string,
  title: string
) =>
  axios
    .put(`${base(user, tournamentId)}/${conversationId}/details`, { title }, config(user))
    .then(response => resource(response.data));
export const assignTournamentConversation = (
  user: AppUser,
  tournamentId: string,
  conversationId: string,
  assigned: boolean
) =>
  axios
    .put(`${base(user, tournamentId)}/${conversationId}/assignment`, { assigned }, config(user))
    .then(response => resource(response.data));
export const editTournamentMessage = (
  user: AppUser,
  tournamentId: string,
  conversationId: string,
  messageId: string,
  text: string
) =>
  axios
    .put(`${base(user, tournamentId)}/${conversationId}/messages/${messageId}`, { text }, config(user))
    .then(response => resource(response.data));
export const deleteTournamentMessage = (
  user: AppUser,
  tournamentId: string,
  conversationId: string,
  messageId: string,
  reason?: string
) =>
  axios
    .delete(`${base(user, tournamentId)}/${conversationId}/messages/${messageId}`, {
      ...config(user),
      data: { reason }
    })
    .then(response => resource(response.data));
export const reportTournamentMessage = (
  user: AppUser,
  tournamentId: string,
  conversationId: string,
  messageId: string,
  reason: string
) =>
  axios
    .post(`${base(user, tournamentId)}/${conversationId}/messages/${messageId}/report`, { reason }, config(user))
    .then(response => resource(response.data));
export const resolveTournamentMessageReport = (
  user: AppUser,
  tournamentId: string,
  conversationId: string,
  messageId: string,
  resolution: 'KEEP' | 'REMOVE'
) =>
  axios
    .put(
      `${base(user, tournamentId)}/${conversationId}/messages/${messageId}/report-resolution`,
      { resolution },
      config(user)
    )
    .then(response => resource(response.data));
export const getTournamentConversationAudit = (user: AppUser, tournamentId: string, conversationId: string) =>
  axios.get(`${base(user, tournamentId)}/${conversationId}/audit`, config(user)).then(response => resourceList(response.data));
export const downloadTournamentSafeguardingReport = async (
  user: AppUser,
  tournamentId: string,
  conversationId: string
) => {
  const response = await axios.get(`${base(user, tournamentId)}/${conversationId}/safeguarding-report`, {
    ...config(user),
    responseType: 'blob'
  });
  const url = window.URL.createObjectURL(response.data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `tournament-communications-${conversationId}.pdf`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
};
